import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../../global/components";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import {
  setProfileCountAction,
  subscriptionAction,
} from "../../../../redux/authSlice";
import { useAppDispatch } from "../../../../utils/hooks";
import promoCodeDialogStyles from "./PromoCodeDialog.styles";
import { setPromoCodeValue } from "./PromoCodeDialogService";

interface PromoCodeProps {
  open: boolean;
  setOpen: Function;
  setIsLoading: Function;
}

interface PromoCode {
  value: string;
  error: string;
}

const PromoCodeDialog = ({ open, setOpen, setIsLoading }: PromoCodeProps) => {
  const classes = promoCodeDialogStyles;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [promoCode, setPromoCode] = useState<PromoCode>({
    value: "",
    error: "",
  });

  const handleValidation = () => {
    if (!promoCode.value) {
      setPromoCode({
        value: promoCode.value,
        error: "Please enter promo code",
      });
      return false;
    }
    return true;
  };

  const handleAddPromoCode = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const response = await setPromoCodeValue(promoCode.value);
        openSuccessNotification(response.message);
        dispatch(
          subscriptionAction({
            isSubscribed: true,
            accessType: response.accessType,
          })
        );
        dispatch(
          setProfileCountAction({
            profileCount: response.profileCount,
          })
        );
        setOpen(false);
        setIsLoading(false);
      } else {
        openErrorNotification(promoCode.error);
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddPromoCode();
    }
  };

  const handlePromoCode = (event: React.ChangeEvent<any>) => {
    setPromoCode({ value: event.target.value, error: "" });
  };

  const handleClose = () => {
    setOpen(false);
    setPromoCode({
      value: "",
      error: "",
    });
  };

  const dialogHeaderContent = () => {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2} pt={5}>
        <Typography sx={classes.lightTypo}>{t("promoCode")}</Typography>
        <Divider
          sx={{
            border: "1px solid #E7E7E7",
            width: "80%",
            height: "0px",
          }}
        />
      </Stack>
    );
  };

  const dialogBodyContent = () => {
    return (
      <Stack
        direction={"column"}
        alignItems={"center"}
        pb={5}
        spacing={2}
        sx={classes.dialogContent}
      >
        <Box mt={2} width={"70%"}>
          <TextField
            sx={classes.promoTextField}
            value={promoCode.value}
            onChange={handlePromoCode}
            onKeyPress={handleKeypress}
          />
          {!isTruthy(promoCode.value) && promoCode.error && (
            <FormHelperText error sx={classes.errorStyling}>
              {promoCode.error}
            </FormHelperText>
          )}
        </Box>
        <Button
          sx={{ ...classes.loginBtn, minWidth: "280px", mt: 2 }}
          onClick={handleAddPromoCode}
        >
          <Typography sx={{ ...classes.loginBtnTypo, fontSize: "15px" }}>
            {t("submit")}
          </Typography>
        </Button>
      </Stack>
    );
  };

  return (
    <CustomDialog
      width="600px"
      borderRadius="10px"
      isDialogOpen={open}
      closable={true}
      closeButtonVisibility={true}
      handleDialogClose={handleClose}
      dialogHeaderContent={dialogHeaderContent()}
      dialogBodyContent={dialogBodyContent()}
    />
  );
};

export default PromoCodeDialog;
